import type { ReactNode } from 'react';
import { cn } from '../cn.ts';

/**
 * Vỏ một section của trang. `tone` là 7 loại nền ĐO ĐƯỢC trong thiết kế, không phải thang màu:
 * `#security` cố ý dùng `surface-black` khác hẳn navy `surface-navy`, nên hai cái là hai tone
 * riêng chứ không phải hai bậc của một ramp.
 *
 * `cv` bật `content-visibility: auto` (design doc §8.4 tầng T1) — trình duyệt bỏ qua
 * style/layout/paint cho section ngoài khung nhìn. KHÔNG bật cho section trên màn đầu:
 * `hero` chứa phần tử LCP.
 *
 * Chiều cao mồi cho `contain-intrinsic-size` KHÔNG đi qua prop nào ở đây: nó khai bằng một class
 * mồi riêng cho từng đơn vị, mỗi bậc màn một giá trị, trong `motion.css`. Lý do là cơ chế, không
 * phải sở thích — chiều cao một section đổi theo khổ màn, mà style inline không mang được
 * `@media` nên một giá trị đơn không phục vụ nổi cả bốn bậc; và inline là tầng riêng của cascade
 * nên nó thắng mọi class, tức một prop như vậy chỉ có thể ghi đè số đã đo.
 *
 * Số trong các class đó là số ĐO, sinh bằng `apps/web/script/measure-section-heights.mjs`, không
 * gõ tay. `600px` mà `motion.css` để làm mặc định là LƯỚI AN TOÀN cho section bật `cv` mà quên
 * khai class mồi — không phải giá trị dùng thật.
 */
export interface SectionProps {
  readonly children: ReactNode;
  /**
   * id để liên kết neo (#bang-gia) — cũng là thứ Google dùng cho "jump to" trong kết quả
   * tìm kiếm, nên đặt id có nghĩa thay vì section-1, section-2.
   */
  readonly id?: string;
  readonly tone?:
    'default' | 'muted' | 'muted-top' | 'navy' | 'black' | 'gradient-ai' | 'gradient-cta';
  readonly size?: 'md' | 'lg';
  /** Gắn `aria-labelledby` vào heading của section để screen reader đọc được ngữ cảnh. */
  readonly labelledBy?: string;
  readonly cv?: boolean;
  readonly className?: string;
}

const TONE = {
  default: 'bg-surface',
  muted: 'bg-surface-muted border-y border-ink-100',
  /*
   * Cùng nền `muted` nhưng CHỈ kẻ trên. Đây là tone thứ 7, và nó tồn tại vì thiết kế có hai kiểu
   * section nền mờ: `#collab` (dòng 812) và `#connectors` (dòng 974) khai cả hai cạnh, còn `#faq`
   * (dòng 1027) khai ĐÚNG một cạnh trên — nó là section nền mờ cuối trang, ngay trên `#cta` nền
   * gradient, nên một nét ở đáy sẽ nằm chồng lên mép gradient.
   *
   * Vì sao là một khoá của bảng chứ không phải một prop `border`: bảng này mang TRỌN chuỗi class
   * của mỗi tone, nên thêm prop là phá kiểu đó và mở đường cho hai utility cùng thuộc tính
   * `border-*` cùng sống — `cn()` chỉ nối chuỗi nên thắng thua do thứ tự Tailwind xếp trong CSS.
   * Cùng lý do khiến `border-b-0` qua `className` là đường SAI, đã bị bác trong plan lô D.
   */
  'muted-top': 'bg-surface-muted border-t border-ink-100',
  navy: 'bg-surface-navy text-white',
  black: 'bg-surface-black text-white',
  'gradient-ai': 'bg-[image:var(--gradient-ai)] text-white',
  'gradient-cta': 'bg-[image:var(--gradient-cta)] text-white',
} as const;

export function Section({
  children,
  id,
  tone = 'default',
  size = 'md',
  labelledBy,
  cv = false,
  className,
}: SectionProps) {
  return (
    <section
      {...(id === undefined ? {} : { id })}
      {...(labelledBy === undefined ? {} : { 'aria-labelledby': labelledBy })}
      className={cn(
        TONE[tone],
        size === 'md' ? 'py-section' : 'py-section-lg',
        // Bù chiều cao header sticky khi nhảy tới anchor — thiết kế header cao 68px (56 ở mobile).
        'scroll-mt-[68px]',
        cv && 'nx-cv',
        className,
      )}
    >
      {children}
    </section>
  );
}
